// ABOUTME: The line count of the user's cart in each dealership, for the badges of the cart's dealership select.
// ABOUTME: Read only for users who have a choice to make, and again each time the shown cart changes.

import { useEffect, useState } from 'react';

import { useOptionalDealershipChoice } from '../../dealership/context/DealershipChoiceContext';
import { needsDealershipChoice } from '../../dealership/utils/dealershipChoice';
import { useCart } from './useCart';

/** Keyed by dealership id, undefined for "None (company)"; a dealership without a cart is absent. */
export type DealershipItemCounts = ReadonlyMap<string | undefined, number>;

const NO_COUNTS: DealershipItemCounts = new Map();

/**
 * Loads the counts, then again whenever `shownCart` changes (a line added or removed, a dealership
 * switched): the cart context answers a new cart object after each of them. A failed read keeps the
 * counts shown.
 */
export function useCartItemCounts(shownCart: unknown): DealershipItemCounts {
    const { getCartItemCounts } = useCart();
    const loadSelectable = useOptionalDealershipChoice()?.loadSelectable;
    const [counts, setCounts] = useState<DealershipItemCounts>(NO_COUNTS);

    useEffect(() => {
        if (!loadSelectable) return undefined;
        // An answer arriving after a newer read started (or after unmounting) is dropped.
        let current = true;
        loadSelectable()
            .then(selectable => {
                // Without a choice the select is hidden: nothing to count for.
                if (!needsDealershipChoice(selectable)) return undefined;
                return getCartItemCounts().then(entries => {
                    if (!current) return;
                    setCounts(
                        new Map(
                            entries.map(entry => [
                                entry.dealershipId ?? undefined,
                                entry.itemCount,
                            ]),
                        ),
                    );
                });
            })
            .catch(() => undefined);
        return () => {
            current = false;
        };
    }, [shownCart, loadSelectable, getCartItemCounts]);

    return counts;
}
